import { Button, Input, Label, Modal, Textarea, toast } from "@devhop/ui";
import { TrashIcon } from "lucide-react";
import { nanoid } from "nanoid";
import { useEffect, useMemo, useState } from "react";
import { computeTotalsFromLineItems } from "@/modules/quotation-invoice/domain/line-totals";
import type {
  ConvertQuotationToInvoiceInput,
  CreateDocumentItemDTO,
  QuotationHeadroom,
} from "@/modules/quotation-invoice/domain/types";

const EPS = 0.005;

type LineRow = {
  id: string;
  description: string;
  detail: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  discountAmount: number;
};

function rowsToDto(rows: LineRow[]): CreateDocumentItemDTO[] {
  return rows
    .filter((r) => r.description.trim() !== "")
    .map((r) => ({
      description: r.description.trim(),
      detail: r.detail.trim() ? r.detail.trim() : null,
      quantity: r.quantity,
      unit: r.unit.trim() || null,
      unitPrice: r.unitPrice,
      discountAmount: Math.max(0, r.discountAmount ?? 0),
      taxRate: 0,
    }));
}

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  quotationHeadroom: QuotationHeadroom;
  defaultItems: CreateDocumentItemDTO[];
  defaultDiscount: number;
  defaultTaxRate: number;
  onSubmit: (body: ConvertQuotationToInvoiceInput) => void;
  isSubmitting?: boolean;
};

export function ConvertInvoicePeriodDialog({
  open,
  onOpenChange,
  quotationHeadroom,
  defaultItems,
  defaultDiscount,
  defaultTaxRate,
  onSubmit,
  isSubmitting,
}: Props) {
  const [rows, setRows] = useState<LineRow[]>([]);
  const [discountAmount, setDiscountAmount] = useState(defaultDiscount);
  const [taxRate, setTaxRate] = useState(defaultTaxRate);
  const [notes, setNotes] = useState("");
  const [dueDate, setDueDate] = useState("");

  useEffect(() => {
    if (!open) return;
    setRows(
      defaultItems.length > 0
        ? defaultItems.map((it) => ({
            id: nanoid(),
            description: it.description,
            detail: it.detail ?? "",
            quantity: it.quantity,
            unit: it.unit ?? "",
            unitPrice: it.unitPrice,
            discountAmount: it.discountAmount ?? 0,
          }))
        : [
            {
              id: nanoid(),
              description: "",
              detail: "",
              quantity: 1,
              unit: "",
              unitPrice: 0,
              discountAmount: 0,
            },
          ],
    );
    setDiscountAmount(defaultDiscount);
    setTaxRate(defaultTaxRate);
    setNotes("");
    setDueDate("");
  }, [open, defaultItems, defaultDiscount, defaultTaxRate]);

  const dtoItems = useMemo(() => rowsToDto(rows), [rows]);
  const computed = useMemo(() => {
    if (dtoItems.length === 0) {
      return { total: 0, subtotal: 0, taxAmount: 0 };
    }
    return computeTotalsFromLineItems(dtoItems, discountAmount, taxRate);
  }, [dtoItems, discountAmount, taxRate]);

  const handleSubmit = () => {
    if (dtoItems.length === 0) {
      toast.error("ກະລຸນາໃສ່ຢ່າງໜ້ອຍໜຶ່ງລາຍການ");
      return;
    }
    if (computed.total > quotationHeadroom.remaining + EPS) {
      toast.error(
        `ຍອດງວດນີ້ເກີນຍອດເຫຼືອ (${quotationHeadroom.remaining.toLocaleString()})`,
      );
      return;
    }
    if (computed.total <= 0) {
      toast.error("ຍອດລວມຕ້ອງຫຼາຍກວ່າ 0");
      return;
    }
    onSubmit({
      items: dtoItems,
      discountAmount,
      taxRate,
      notes: notes.trim() || null,
      dueDate: dueDate.trim() || null,
    });
  };

  const removeRow = (rowId: string) => {
    setRows((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((r) => r.id !== rowId);
    });
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="ອອກ Invoice ງວດຕໍ່ໄປ"
      size="lg"
    >
      <div className="max-h-[70vh] space-y-4 overflow-y-auto pr-1">
        <p className="text-muted-foreground text-sm">
          ຍອດເຫຼືອຈາກໃບສະເໜີ:{" "}
          <span className="font-medium text-foreground tabular-nums">
            {quotationHeadroom.remaining.toLocaleString()}
          </span>{" "}
          — ລວມ Invoice ອອກແລ້ວ {quotationHeadroom.invoicedSum.toLocaleString()}
        </p>
        <div className="grid gap-2 sm:grid-cols-2">
          <div>
            <Label htmlFor="conv-discount">ສ່ວນຫຼຸດເອກະສານ</Label>
            <Input
              id="conv-discount"
              type="number"
              min={0}
              step="0.01"
              value={discountAmount}
              onChange={(e) => setDiscountAmount(Number(e.target.value) || 0)}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="conv-tax">ອັດຕາ VAT (%)</Label>
            <Input
              id="conv-tax"
              type="number"
              min={0}
              max={100}
              step={0.01}
              value={Math.round(taxRate * 10000) / 100}
              onChange={(e) =>
                setTaxRate(
                  Math.min(1, Math.max(0, (Number(e.target.value) || 0) / 100)),
                )
              }
              className="mt-1"
              aria-label="ອັດຕາ VAT ເປີເຊັນ"
            />
          </div>
        </div>
        <div>
          <Label htmlFor="conv-due">ວັນຄົບກຳນົດຊຳລະງວດນີ້</Label>
          <Input
            id="conv-due"
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="conv-notes">ຫມາຍເຫດງວດນີ້</Label>
          <Textarea
            id="conv-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="mt-1"
          />
        </div>
        <div className="overflow-x-auto rounded border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/40 text-left">
                <th className="min-w-[120px] p-2">ລາຍການ</th>
                <th className="min-w-[100px] max-w-[160px] p-2">ລາຍລະອຽດ</th>
                <th className="w-20 p-2 text-right">ຈຳນວນ</th>
                <th className="w-24 p-2">ຫນ່ວຍ</th>
                <th className="w-28 p-2 text-right">ລາຄາຕໍ່ຫນ່ວຍ</th>
                <th className="w-28 p-2 text-right">ສ່ວນຫຼຸດ</th>
                <th className="w-10 p-2 text-center" aria-label="ລຶບລາຍການ" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-b">
                  <td className="p-1 align-top">
                    <Input
                      value={row.description}
                      onChange={(e) => {
                        const v = e.target.value;
                        setRows((prev) =>
                          prev.map((r) =>
                            r.id === row.id ? { ...r, description: v } : r,
                          ),
                        );
                      }}
                      placeholder="ລາຍການ"
                      className="h-8"
                    />
                  </td>
                  <td className="max-w-[160px] p-1 align-top">
                    <Textarea
                      value={row.detail}
                      onChange={(e) => {
                        const v = e.target.value;
                        setRows((prev) =>
                          prev.map((r) =>
                            r.id === row.id ? { ...r, detail: v } : r,
                          ),
                        );
                      }}
                      placeholder="ບໍ່ບັງຄັບ"
                      className="min-h-[44px] text-xs"
                      rows={2}
                    />
                  </td>
                  <td className="p-1">
                    <Input
                      type="number"
                      min={0}
                      step="0.0001"
                      value={row.quantity}
                      onChange={(e) => {
                        const v = Number(e.target.value) || 0;
                        setRows((prev) =>
                          prev.map((r) =>
                            r.id === row.id ? { ...r, quantity: v } : r,
                          ),
                        );
                      }}
                      className="h-8 text-right"
                    />
                  </td>
                  <td className="p-1">
                    <Input
                      value={row.unit}
                      onChange={(e) => {
                        const v = e.target.value;
                        setRows((prev) =>
                          prev.map((r) =>
                            r.id === row.id ? { ...r, unit: v } : r,
                          ),
                        );
                      }}
                      placeholder="ຫນ່ວຍ"
                      className="h-8"
                    />
                  </td>
                  <td className="p-1">
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      value={row.unitPrice}
                      onChange={(e) => {
                        const v = Number(e.target.value) || 0;
                        setRows((prev) =>
                          prev.map((r) =>
                            r.id === row.id ? { ...r, unitPrice: v } : r,
                          ),
                        );
                      }}
                      className="h-8 text-right"
                    />
                  </td>
                  <td className="p-1">
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      value={row.discountAmount}
                      onChange={(e) => {
                        const v = Number(e.target.value) || 0;
                        setRows((prev) =>
                          prev.map((r) =>
                            r.id === row.id ? { ...r, discountAmount: v } : r,
                          ),
                        );
                      }}
                      placeholder="0"
                      className="h-8 text-right"
                    />
                  </td>
                  <td className="p-1 align-middle">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      onClick={() => removeRow(row.id)}
                      disabled={rows.length <= 1}
                      aria-label="ລຶບລາຍການ"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() =>
            setRows((prev) => [
              ...prev,
              {
                id: nanoid(),
                description: "",
                detail: "",
                quantity: 1,
                unit: "",
                unitPrice: 0,
                discountAmount: 0,
              },
            ])
          }
        >
          ເພີ່ມລາຍການ
        </Button>
        <div className="flex justify-between border-t pt-2 text-sm">
          <span className="text-muted-foreground">ຍອດລວມງວດນີ້ (ຄາດ):</span>
          <span className="font-semibold tabular-nums">
            {computed.total.toLocaleString()}
          </span>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            ຍົກເລີກ
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? "ກຳລັງສ້າງ..." : "ສ້າງ Invoice ງວດນີ້"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
